import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from "@/supabase/types"
import { TablesInsert, TablesUpdate } from "@/supabase/types"
import { PermAction, PermOptions, TableWithPermissions } from '@/types/base/permissions'

import { evaluatePermissions, Role } from '@/types/base/authuser';
import Authuser from '@/types/base/authuser';
import { isAidonAdmin } from './tenants';
import { cache } from 'react';
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'
import { printObject } from '@/lib/utils'
import { getWorkspaceById } from './workspaces';

const logger = createIsomorphicLogger('db.providers')


export const getProviderById = async (supabase: SupabaseClient<Database>, providerId: string) => {
  const { data: provider, error } = await supabase
    .from("providers")
    .select("*")
    .eq("id", providerId)
    .single()

  if (!provider) {
    logError(logger, error, 'Failed to fetch Provider by ID', { providerId })
    throw new Error(error.message)
  }

  return provider
}

export const getOptionalProviderById = async (supabase: SupabaseClient<Database>, providerId: string) => {
  const { data: provider, error } = await supabase
    .from("providers")
    .select("*")
    .eq("id", providerId)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Provider by ID', { providerId })
    throw new Error(error.message)
  }

  return provider
}

export const getOptionalProviderByName = async (supabase: SupabaseClient<Database>, providerName: string) => {
  const { data: provider, error } = await supabase
    .from("providers")
    .select("*")
    .eq("name", providerName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Provider by name', { providerName })
    throw new Error(error.message)
  }

  return provider
}

export const getOptionalProviderByInternalName = async (supabase: SupabaseClient<Database>, internalName: string) => {
  const { data: provider, error } = await supabase
    .from("providers")
    .select("*")
    .eq("internal_name", internalName)
    .maybeSingle()

  if (error) {
    logError(logger, error, 'Failed to fetch optional Provider by internal name', { internalName })
    throw new Error(error.message)
  }

  return provider
}

export const getAidonProvider = cache(async (supabase: SupabaseClient<Database>) => {
	const aidonProvider = await getOptionalProviderByInternalName(supabase, 'aidon')
  return aidonProvider
})

export const getAllProviders = async (supabase: SupabaseClient<Database>) => {
  const { data: providers, error } = await supabase
    .from("providers")
    .select("*")

  if (!providers) {
    logError(logger, error, 'Failed to fetch all Providers')
    throw new Error(error.message)
  }

  return providers
}

export const getAllProvidersWithPermissions = async (
	supabase: SupabaseClient<Database>,
	authuser: Authuser,
	workspaceId: string
) : Promise<TableWithPermissions<'providers'>[]> => {
	const allProviders = await getAllProviders(supabase)
	const workspace = await getWorkspaceById(supabase, workspaceId)
	const result: TableWithPermissions<'providers'>[] = []

	for (const provider of allProviders) {
		const perms = await evaluatePermissions(supabase, authuser,
				{ type: "providers", context: "ANY", uuid: provider.id, ws: workspace})

		result.push({ ...provider, permissions: perms })
	}

  return result
}

export const getAllDefinedProviders = async (supabase: SupabaseClient<Database>) => {
  const { data: providers, error } = await supabase
    .from("providers")
    .select("*")
		.eq("load_via_api", false)

  if (!providers) {
    logError(logger, error, 'Failed to fetch defined Providers')
    throw new Error(error.message)
  }

  return providers
}

export const createProvider = async (
	supabase: SupabaseClient<Database>,
  provider: TablesInsert<"providers">
) => {
  const { data: createdProvider, error } = await supabase
    .from("providers")
    .insert([provider])
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to create Provider', { provider: printObject(provider) })
    throw new Error(error.message)
  }

  logger.info('Provider created successfully', { providerId: createdProvider.id })
  return createdProvider
}

export const createProviders = async (
	supabase: SupabaseClient<Database>,
  providers: TablesInsert<"providers">[]
) => {
  const { data: createdProviders, error } = await supabase
    .from("providers")
    .insert(providers)
    .select("*")

  if (error) {
    logError(logger, error, 'Failed to create Providers', { providers: printObject(providers) })
    throw new Error(error.message)
  }

  logger.info('Providers created successfully', { providers: printObject(providers) })
  return createdProviders
}

export const updateProvider = async (
	supabase: SupabaseClient<Database>,
  providerId: string,
  provider: TablesUpdate<"providers">
) => {
  const { data: updatedProvider, error } = await supabase
    .from("providers")
    .update(provider)
    .eq("id", providerId)
    .select("*")
    .single()

  if (error) {
    logError(logger, error, 'Failed to update Provider', { providerId, provider: printObject(provider) })
    throw new Error(error.message)
  }

  return updatedProvider
}

export const deleteProvider = async (supabase: SupabaseClient<Database>, providerId: string) => {
  const { error } = await supabase.from("providers").delete().eq("id", providerId)

  if (error) {
    logError(logger, error, 'Failed to delete Provider', { providerId })
    throw new Error(error.message)
  }

  return true
}

//Providers can only be viewed and edited by Aiadmin.
//All other operations are reserved for root
export const providerCanPerform = async (supabase: SupabaseClient<Database>, authuser: Authuser, options: PermOptions) => {
	if (authuser.isAuthorizedForRole(Role.root)) {
		return true
	}

	if (options.context == 'IMPEXP' || options.context == 'Folder') {
		return isAidonAdmin(supabase, authuser)
	}

	switch (options.action) {
		default:  //includes create
			return isAidonAdmin(supabase, authuser)
	}
}
